import { useEffect, useRef, useState, startTransition } from "react";
import { Save, CheckCircle, CircleAlert, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, ConfirmModal, ALERT_BANNER_SURFACE_CLASS } from "@/components/ui";
import { cn } from "@/lib/utils";

export type SaveBarPhase = "unsaved" | "success";

interface SaveBarProps {
  phase: SaveBarPhase | "hidden";
  isPending: boolean;
  formId: string;
  onRevert?: () => void;
  submitLabel?: string;
  secondaryAction?: { label: string; onClick: () => void };
}

export function SaveBar({
  phase,
  isPending,
  formId,
  onRevert,
  submitLabel,
  secondaryAction,
}: SaveBarProps) {
  const { t } = useTranslation("common");
  const [presentedPhase, setPresentedPhase] = useState<SaveBarPhase | null>(
    () => (phase === "hidden" ? null : phase),
  );
  const [isShown, setIsShown] = useState(phase !== "hidden");
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const prevPhaseRef = useRef(phase);

  useEffect(() => {
    const prevPhase = prevPhaseRef.current;
    prevPhaseRef.current = phase;

    if (phase !== "hidden") {
      startTransition(() => {
        setPresentedPhase(phase);
      });

      if (prevPhase === "hidden") {
        startTransition(() => setIsShown(false));
        const frame = requestAnimationFrame(() => {
          requestAnimationFrame(() => setIsShown(true));
        });
        return () => cancelAnimationFrame(frame);
      }

      startTransition(() => setIsShown(true));
      return;
    }

    if (prevPhase !== "hidden") {
      startTransition(() => setIsShown(false));
    }
  }, [phase]);

  const handleTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    // Tailwind 4 animates translate-y-* through the CSS `translate` property,
    // not `transform`; opacity always animates, so its end marks the exit.
    if (
      event.currentTarget !== event.target ||
      event.propertyName !== "opacity" ||
      isShown ||
      phase !== "hidden"
    ) {
      return;
    }

    setPresentedPhase(null);
  };

  if (presentedPhase === null) {
    return null;
  }

  const isSuccess = presentedPhase === "success";

  return (
    <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-40 lg:left-64">
      <div className="pointer-events-auto mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
        <div
          className={cn(
            "mx-auto max-w-5xl transition-[translate,opacity] duration-300 ease-in-out",
            isShown ? "translate-y-0 opacity-100" : "translate-y-full opacity-0",
          )}
          onTransitionEnd={handleTransitionEnd}
        >
          <div className="px-4 pt-6 pb-4 sm:px-6">
            <div
              className={cn(
                "flex flex-wrap items-center gap-3 rounded-xl border p-3 pl-4 shadow-popover backdrop-blur-md transition-colors duration-300 ease-out",
                isSuccess ? "justify-center" : "justify-between",
                isSuccess
                  ? ALERT_BANNER_SURFACE_CLASS.success
                  : ALERT_BANNER_SURFACE_CLASS.warning,
              )}
            >
              <div
                className={cn(
                  "flex items-center gap-2",
                  !isSuccess && "min-w-0 flex-1",
                )}
              >
                {isSuccess ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-success-500" />
                    <span className="text-sm font-medium text-success-700">
                      {t("saveBar.allSaved")}
                    </span>
                  </>
                ) : (
                  <>
                    <CircleAlert className="h-5 w-5 text-warning-500" />
                    <span className="text-sm font-medium text-warning-700">
                      {t("saveBar.unsaved")}
                    </span>
                  </>
                )}
              </div>
              {!isSuccess && (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {onRevert && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowRevertConfirm(true)}
                      disabled={isPending}
                      leftIcon={<RotateCcw className="h-4 w-4" />}
                      className="!border-0 bg-surface-0 text-error-600 hover:!border-error-600 hover:bg-error-50 hover:text-error-700 focus-visible:ring-error-500"
                    >
                      {t("saveBar.revert")}
                    </Button>
                  )}
                  {secondaryAction ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isPending}
                      leftIcon={<Save className="h-4 w-4" />}
                      onClick={secondaryAction.onClick}
                    >
                      {secondaryAction.label}
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    form={formId}
                    isLoading={isPending}
                    leftIcon={<Save className="h-4 w-4" />}
                  >
                    {submitLabel ?? t("saveBar.save")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        closeLabel={t("close")}
        cancelText={t("cancel")}
        isOpen={showRevertConfirm}
        onClose={() => setShowRevertConfirm(false)}
        onConfirm={() => {
          setShowRevertConfirm(false);
          onRevert?.();
        }}
        title={t("saveBar.revertConfirmTitle")}
        message={t("saveBar.revertConfirmMessage")}
        confirmText={t("saveBar.revertConfirm")}
        isDestructive
      />
    </div>
  );
}
